import * as Yup from 'yup';
import { AppDispatch } from '@/app/store';
import { ButtonLink } from '@/components/common/ButtonLink';
import { CheckIcon } from '@/app/icons/CheckIcon';
import { EyeCrossedIcon } from '@/app/icons/EyeCrossedIcon';
import { EyeIcon } from '@/app/icons/EyeIcon';
import { FORBIDDEN, LOCKED, TOO_MANY_REQUESTS, UNAUTHORIZED } from '@/constants/HttpStatus';
import { Formik, FormikErrors, FormikHelpers, FormikProps } from 'formik';
import { Grid, IconButton, InputAdornment, Stack, Typography, useMediaQuery } from '@mui/material';
import { LoadingButton } from '@/components/common/button/LoadingButton';
import {
  MESSAGE_CAPTCHA_FAILURE,
  MESSAGE_INVALID_CREDENTIALS,
  MESSAGE_REQUIRED,
  MESSAGE_UNKNOWN,
  MESSAGE_USER_DISABLED,
  MESSAGE_USER_DISABLED_PHONE_LOGIN,
  formatLoginThrottled,
} from '@/utils/validation-utils';
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import { OutlinedField } from '@/components/common/field/OutlinedField';
import {
  ROUTE_AUTHENTICATION_LOCKED,
  ROUTE_INBOX,
  ROUTE_MAIN,
  ROUTE_REGISTRATION,
  ROUTE_RESTORE,
  getRegistrationConfirmRequestRoute,
} from '@/app/routes';
import { SignInDTO } from '@/types/auth/SignInDTO';
import { Theme } from '@mui/material/styles';
import { UserDTO } from '@/types/user/UserDTO';
import { emailSchema } from '@/validation/emailSchema';
import { getCurrentUser, signIn, signOut } from '@/services/authSlice';
import { getNewEmail, setNewEmail } from '@/services/profileSlice';
import { isBlank } from '@/utils/string-utils';
import { loginIdentifierSchema } from '@/validation/loginIdentifierSchema';
import { toCanonicalLoginPhone } from '@/utils/phone-utils';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useReCaptcha } from '@/app/hooks/useReCaptcha';
import { useSignInMutation } from '@/services/api/authApiSlice';
import { useSnackbar } from 'notistack';
import React, { ChangeEvent, FC, MouseEvent, ReactElement, useEffect, useMemo, useState } from 'react';
import type { AuthErrorCode } from '@/types/auth/AuthErrorCode';

interface FormValues {
  login?: string;
  password?: string;
}

/**
 * The rejected sign-in response, narrowed to what this form branches on.
 */
interface SignInError {
  status: number;
  data?: {
    code?: AuthErrorCode;
    secondsRemaining?: number;
  };
}

export const AuthenticationForm: FC = (): ReactElement => {
  const dispatch: AppDispatch = useAppDispatch();
  const { state } = useLocation();
  const navigate: NavigateFunction = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { pending: captchaPending, obtainToken } = useReCaptcha('signInAction');

  const authUser: UserDTO | undefined = useAppSelector(getCurrentUser);
  const isNewEmailConfirm: boolean = useAppSelector(getNewEmail);

  const [login, { isLoading }] = useSignInMutation();

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const isMobile: boolean = useMediaQuery((theme: Theme): string => theme.breakpoints.down('sm'));

  const initialValues: FormValues = useMemo<FormValues>(() => {
    const params: URLSearchParams = new URLSearchParams(location.search);
    return {
      login: params.get('email') ?? '',
      password: '',
    };
  }, []);
  const initialErrors: FormikErrors<FormValues> = {
    login: isBlank(initialValues.login) ? MESSAGE_REQUIRED : undefined,
    password: isBlank(initialValues.password) ? MESSAGE_REQUIRED : undefined,
  };
  const validationSchema: Yup.SchemaOf<FormValues> = Yup.object({
    login: loginIdentifierSchema.required(MESSAGE_REQUIRED).nullable(),
    password: Yup.string().required(MESSAGE_REQUIRED).nullable(),
  });

  useEffect((): void => {
    if (authUser) {
      navigate(state?.redirectTo ?? ROUTE_INBOX, { replace: true });
    }
  }, [authUser, navigate, state]);

  const handleShowPasswordClick = (): void => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (values: FormValues, helpers: FormikHelpers<FormValues>): Promise<void> => {
    const token: string | null = await obtainToken();
    if (!token) {
      return;
    }
    const dto: SignInDTO = {
      username: toCanonicalLoginPhone(values.login || ''),
      password: values.password || '',
    };
    await login({ dto: dto, reCaptchaToken: token })
      .unwrap()
      .then((user: UserDTO) => {
        dispatch(signIn(user));
        dispatch(setNewEmail(false));
        navigate(state?.redirectTo ?? ROUTE_MAIN, { replace: true });
      })
      .catch((e: SignInError) => {
        const { setFieldError } = helpers;
        if (e.data?.code === 'RECAPTCHA_REJECTED') {
          // The credentials were never examined, so neither field is at fault and neither is marked.
          enqueueSnackbar(MESSAGE_CAPTCHA_FAILURE, {
            variant: 'error',
            preventDuplicate: true,
          });
          return;
        }
        if (e.status === TOO_MANY_REQUESTS) {
          // Refused before the credentials were examined, so neither field is at fault and neither is marked.
          // The seconds are absent when the balancer shed the request, which sends no body of its own.
          enqueueSnackbar(formatLoginThrottled(e.data?.secondsRemaining), {
            variant: 'error',
            preventDuplicate: true,
          });
          return;
        }
        if (e.status === UNAUTHORIZED) {
          setFieldError('login', '');
          setFieldError('password', MESSAGE_INVALID_CREDENTIALS);
        } else if (e.status === FORBIDDEN) {
          const isEmailLogin: boolean = Boolean(values.login) && emailSchema.isValidSync(values.login);
          setFieldError('login', '');
          setFieldError('password', isEmailLogin ? MESSAGE_USER_DISABLED : MESSAGE_USER_DISABLED_PHONE_LOGIN);
          if (isEmailLogin) {
            navigate(getRegistrationConfirmRequestRoute(values.login as string));
          }
        } else if (e.status === LOCKED) {
          navigate(ROUTE_AUTHENTICATION_LOCKED);
        } else {
          setFieldError('login', '');
          setFieldError('password', MESSAGE_UNKNOWN);
        }
        setError(true);
        dispatch(signOut());
      });
  };

  return (
    <Formik
      initialValues={initialValues}
      initialErrors={initialErrors}
      enableReinitialize={true}
      validationSchema={validationSchema}
      validateOnChange={true}
      onSubmit={handleSubmit}>
      {({ values, touched, errors, setFieldValue, setFieldTouched, submitForm }: FormikProps<FormValues>) => {
        const handleSetFieldValue = (e: ChangeEvent<HTMLInputElement>) => {
          setFieldTouched(e.target.name, true, false);
          setFieldValue(e.target.name, e.target.value, true);
          setError(false);
        };
        return (
          <Grid container={true} direction={'column'} alignItems={'center'} spacing={5}>
            {isNewEmailConfirm ? (
              <Grid item={true}>
                <Grid container={true} direction={'column'} alignItems={'center'} spacing={1}>
                  <Grid item={true}>
                    <CheckIcon
                      sx={{
                        width: (theme: Theme) => theme.spacing(4),
                        height: (theme: Theme) => theme.spacing(4),
                        display: 'block',
                        color: (theme: Theme) => theme.colors.green,
                      }}
                    />
                  </Grid>
                  <Grid item={true}>
                    <Typography variant={'h1'} align={'center'}>
                      {'Адрес электронной почты обновлён!'}
                    </Typography>
                  </Grid>
                  <Grid item={true}>
                    <Typography variant={'h1'} align={'center'}>
                      {'Используйте новый адрес и новый пароль для входа:'}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            ) : (
              <Grid item={true}>
                <Grid container={true} direction={'column'} alignItems={'center'} spacing={isMobile ? 0.5 : 2}>
                  <Grid item={true}>
                    <Typography
                      variant={'h1'}
                      sx={(theme: Theme) => ({
                        lineHeight: '20px',
                        [theme.breakpoints.down('sm')]: {
                          lineHeight: '32px',
                        },
                      })}>
                      {'Вход в Личный Кабинет'}
                    </Typography>
                  </Grid>
                  <Grid item={true}>
                    <Stack direction={'column'} alignItems={'center'}>
                      <Typography variant={'body2'}>{'Еще нет личного кабинета?'}</Typography>
                      <ButtonLink color={'primary'} to={ROUTE_REGISTRATION} state={state} noHover={true}>
                        <Typography variant={'body2'} fontWeight={500}>
                          {'Зарегистрироваться'}
                        </Typography>
                      </ButtonLink>
                    </Stack>
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid item={true} width={'100%'}>
              <Grid container={true} direction={'column'} spacing={3}>
                <Grid item={true} width={'100%'}>
                  <OutlinedField
                    fullWidth={true}
                    name={'login'}
                    label={'Адрес эл. почты или телефон'}
                    withLabel={true}
                    required={true}
                    value={values.login}
                    error={error || (touched.login && Boolean(errors.login))}
                    helperText={touched.login ? errors.login : ''}
                    onChange={handleSetFieldValue}
                  />
                </Grid>
                <Grid item={true} width={'100%'}>
                  <OutlinedField
                    fullWidth={true}
                    name={'password'}
                    label={'Пароль'}
                    withLabel={true}
                    required={true}
                    type={showPassword ? 'text' : 'password'}
                    value={values.password}
                    error={error || (touched.password && Boolean(errors.password))}
                    helperText={touched.password ? errors.password : ''}
                    endAdornment={
                      <InputAdornment position={'end'}>
                        <IconButton
                          aria-label={'toggle password visibility'}
                          size={'dense'}
                          edge={'end'}
                          sx={{ padding: 0, margin: 0 }}
                          onClick={handleShowPasswordClick}
                          onMouseDown={(e: MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                          }}>
                          {showPassword ? (
                            <EyeIcon
                              sx={{
                                width: (theme: Theme) => theme.spacing(4),
                                height: (theme: Theme) => theme.spacing(4),
                              }}
                            />
                          ) : (
                            <EyeCrossedIcon
                              sx={{
                                width: (theme: Theme) => theme.spacing(4),
                                height: (theme: Theme) => theme.spacing(4),
                              }}
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                    onChange={handleSetFieldValue}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item={true} width={'100%'}>
              <Grid container={true} direction={'column'} alignItems={'center'} spacing={3}>
                <Grid item={true} width={'100%'}>
                  <LoadingButton
                    variant={'contained'}
                    color={'primary'}
                    fullWidth={true}
                    type={'submit'}
                    onClick={submitForm}
                    isLoading={isLoading || captchaPending}
                    disabled={isLoading || !values.login || !values.password}
                    data-testid={'sign-in'}>
                    {'Войти'}
                  </LoadingButton>
                </Grid>
                <Grid item={true}>
                  <ButtonLink color={'primary'} to={ROUTE_RESTORE}>
                    <Typography variant={'body2'}>{'Восстановить пароль'}</Typography>
                  </ButtonLink>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        );
      }}
    </Formik>
  );
};
